import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import {
  fundProductionBudget,
  getProduction,
  getProductionBudget,
  type Production,
  type ProductionBudgetView,
} from '@/api/studio-production'
import { useProductionOwner } from '@/features/studio/hooks/useProductionPermissions'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import {
  PRODUCTION_BUDGET_LOW_THRESHOLD,
  productionBudgetAlertKind,
  productionBudgetRemaining,
  type ProductionBudgetAlertKind,
} from '@/features/studio/lib/production-budget'
import { productionBasePath } from '@/features/studio/lib/production-path'
import { ProtoBtn, ProtoInput, ProtoLabel, ProtoModal } from '@/features/studio/components/ui-kit'

export function ProductionBudgetGate({ productionId }: { productionId: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [production, setProduction] = useState<Production | null>(null)
  const [budget, setBudget] = useState<ProductionBudgetView | null>(null)
  const [kind, setKind] = useState<ProductionBudgetAlertKind | null>(null)
  const [open, setOpen] = useState(false)
  const [fundAmount, setFundAmount] = useState('1000')
  const [fundNote, setFundNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isOwner = useProductionOwner(production)

  useEffect(() => {
    if (!productionId) return
    let cancelled = false
    setKind(null)
    setOpen(false)
    setError(null)
    setBudget(null)
    setProduction(null)
    void (async () => {
      try {
        const [prod, view] = await Promise.all([getProduction(productionId), getProductionBudget(productionId)])
        if (cancelled) return
        setProduction(prod)
        setBudget(view)
        const next = productionBudgetAlertKind(view)
        setKind(next)
        setOpen(next != null)
      } catch {
        /* 预算拉失败不阻断进入项目 */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [productionId])

  function handleClose() {
    if (busy) return
    setOpen(false)
    setError(null)
  }

  function goBudgetTab() {
    handleClose()
    navigate(`${productionBasePath(productionId)}/team?tab=budget`)
  }

  async function handleFund() {
    const amount = Math.floor(Number(fundAmount))
    if (!Number.isFinite(amount) || amount <= 0) {
      setError(t('budget.amountInvalid'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const res = await fundProductionBudget(productionId, {
        amount,
        note: fundNote.trim() || undefined,
      })
      setBudget(res)
      setKind(productionBudgetAlertKind(res))
      setFundAmount('1000')
      setFundNote('')
      setOpen(false)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'budget.fundFailed'))
    } finally {
      setBusy(false)
    }
  }

  if (!open || !kind) return null

  const remaining = budget ? productionBudgetRemaining(budget) : 0
  const title = kind === 'missing' ? t('budget.gateMissingTitle') : t('budget.gateLowTitle')
  const body =
    kind === 'missing'
      ? t('budget.gateMissingBody')
      : t('budget.gateLowBody', { balance: remaining, threshold: PRODUCTION_BUDGET_LOW_THRESHOLD })

  return (
    <ProtoModal
      open={open}
      onClose={handleClose}
      title={title}
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={handleClose} disabled={busy}>
            {t('budget.gateLater')}
          </ProtoBtn>
          <ProtoBtn size="sm" variant="secondary" onClick={goBudgetTab} disabled={busy}>
            {t('budget.gateGoBudget')}
          </ProtoBtn>
          {isOwner ? (
            <ProtoBtn size="sm" disabled={busy} onClick={() => void handleFund()}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('budget.fundConfirm')}
            </ProtoBtn>
          ) : null}
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-zinc-400">{body}</p>
        {kind === 'low' && budget ? (
          <p className="text-xs text-zinc-500">{t('budget.fundedTotal', { n: budget.total_funded })}</p>
        ) : null}
        {isOwner ? (
          <>
            <p className="text-[11px] text-zinc-500">{t('budget.fundHint')}</p>
            <div>
              <ProtoLabel required>{t('budget.amount')}</ProtoLabel>
              <ProtoInput
                type="number"
                min={1}
                className="mt-1.5"
                value={fundAmount}
                onChange={(e) => setFundAmount(e.target.value)}
              />
            </div>
            <div>
              <ProtoLabel>{t('budget.note')}</ProtoLabel>
              <ProtoInput className="mt-1.5" value={fundNote} onChange={(e) => setFundNote(e.target.value)} />
            </div>
          </>
        ) : (
          <p className="text-sm text-amber-200/90">{t('budget.gateContactOwner')}</p>
        )}
        {error ? <p className="text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}
      </div>
    </ProtoModal>
  )
}
